import { captureException } from '@sentry/react';
import { Invoice } from '@thro-types/billing';

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { usePdfCreateInvoiceMutation } from 'api/hooks/billing/invoices';
import { translations } from 'locales/i18n';

import { AlertActions } from '../Dashboard/Alert/slice';
import { getInvoicePdfData } from './utils';

export const useFetchPdf = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { create } = usePdfCreateInvoiceMutation({
    params: {
      preview: true
    }
  });
  const [pdfData, setPdfData] = useState<ArrayBuffer | string | null>(null);
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setPdfData(null);
    setOpen(false);
  };

  const fetchPdf = async (invoice: Invoice) => {
    try {
      const pdfData = getInvoicePdfData(invoice);

      const pdfResponse = await create.mutateAsync(pdfData);
      const pdfBlob = new Blob([pdfResponse], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfData(pdfUrl);
      setOpen(true);
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.billing.alerts.pdfLoadingError())));
      setOpen(false);
      setPdfData(null);
    }
  };

  const handlePreview = useCallback(
    async (invoice: Invoice) => {
      try {
        await fetchPdf(invoice);
        if (pdfData) setOpen(true);
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.billing.alerts.pdfLoadingError())));
        captureException(error);
      }
    },
    [fetchPdf, pdfData, setOpen]
  );

  return { pdfData, open, handleClose, handlePreview };
};
